import React, {useEffect, useMemo, useState} from 'react';

import {MAX_ANTIBODIES_TO_CREATE} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import {ItemListingSchemasQuery} from 'nav/sidepanel/listing/__generated/ItemListingSchemasQuery';
import {SchemasFilterFunction, ItemListingSchemasByType} from 'nav/sidepanel/listing/ItemListingUtil';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {FilterKey, keyToEmptyFilter, Operator} from 'search/SearchConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

function getFilterToPairSchemaFunction(pairSchemaId: string, registryId: string): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    for (const type of Object.keys(schemasByAvailableType)) {
      const tagSchemas = schemasByAvailableType[type] as Array<
        TypesFilterSchemasQuery['tagSchemas'][number] | ItemListingSchemasQuery['tagSchemas'][number]
      >;
      const componentSchemas = tagSchemas.filter(
        (schema) => schema.id === pairSchemaId && schema.registry.id === registryId
      );
      filteredSchemas[type] = componentSchemas;
    }

    return filteredSchemas;
  };
}

const AVAILABLE_TYPES: Array<OligoAwareFolderItemType> = [ModelTypeConstants.BASIC_FOLDER_ITEM];

interface SearchForDnaPairsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (selectedIds: Array<string>) => void;
  registryId: string;
  pairSchemaId: string;
}

export default function SearchForDnaPairsModal({
  onRequestClose,
  onRequestSubmit,
  registryId,
  pairSchemaId,
}: SearchForDnaPairsModalProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  // The memoization below is necessary to maintain the scroll state by preventing unnecessary re-renders
  const initialFilters = useMemo(
    () => [
      {
        ...keyToEmptyFilter(FilterKey.SCHEMA),
        operator: Operator.IS_ONE_OF,
        value: [pairSchemaId],
        locked: true,
      },
    ],
    [pairSchemaId]
  );
  useEffect(() => {
    clearBioentitySearchViewSearchParams(
      AVAILABLE_TYPES,
      Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE,
      initialFilters
    );
  }, [initialFilters]);

  return (
    <Modal
      fullWidth={true}
      fullHeight={true}
      scrollable={false}
      title="Search for pairs"
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Select',
          style: ButtonStyle.SECONDARY,
          submit: true,
          disabled: selectedIds.length === 0,
        },
      ]}
      onRequestClose={() => {
        setSelectedIds([]);
        onRequestClose();
      }}
      onRequestSubmit={() => {
        onRequestSubmit(selectedIds);
      }}
    >
      <BioentitySearchView
        limitSelectedEntitiesParams={{
          maxNumEntitiesToAdd: MAX_ANTIBODIES_TO_CREATE,
          selectedEntityName: 'pairs',
        }}
        navSection={Section.ANTIBODY_NESTED_DATA_CREATION}
        availableTypes={AVAILABLE_TYPES}
        selectedBioentityIds={selectedIds}
        setSelectedBioentityIds={setSelectedIds}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        initialFilters={initialFilters}
        filterSchemas={getFilterToPairSchemaFunction(pairSchemaId, registryId)}
      />
    </Modal>
  );
}
